
import React, { useMemo, useState, useEffect } from 'react';
import { Clock, Tv, ShieldAlert, X, Box, Timer, Navigation } from 'lucide-react';
import { Order, OrderStatus, Client, Priority } from './types';
import { Badge } from './SharedComponents';

export const ExpeditionTerminalPage = ({ orders, clients, onClose }: { orders: Order[], clients: Client[], onClose: () => void }) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const terminalOrders = useMemo(() => {
    return orders.filter(o => 
      o.status === OrderStatus.CONFIRMADO || 
      o.status === OrderStatus.EM_CARREGAMENTO ||
      (o.status === OrderStatus.PENDENTE && o.prioridade === Priority.ALTA)
    ).sort((a, b) => {
      if (a.prioridade === Priority.ALTA && b.prioridade !== Priority.ALTA) return -1;
      if (a.prioridade !== Priority.ALTA && b.prioridade === Priority.ALTA) return 1;
      return a.horarioEntrega.localeCompare(b.horarioEntrega);
    });
  }, [orders]);

  const urgentCount = terminalOrders.filter(o => o.prioridade === Priority.ALTA).length;

  const getSLAData = (targetTime: string) => {
    const [hours, minutes] = targetTime.split(':').map(Number);
    const target = new Date();
    target.setHours(hours, minutes, 0, 0);
    
    const diff = target.getTime() - time.getTime();
    const diffMinutes = Math.floor(diff / 60000);
    
    if (diffMinutes < 0) {
      return { text: 'ATRASADO', sub: `${Math.abs(diffMinutes)}m`, color: 'text-red-600', bg: 'bg-red-50', iconColor: 'text-red-500' };
    }
    if (diffMinutes < 30) {
      return { text: 'CRÍTICO', sub: `${diffMinutes}m`, color: 'text-amber-600', bg: 'bg-amber-50', iconColor: 'text-amber-500' };
    }
    return { text: 'PRAZO', sub: `${diffMinutes}m`, color: 'text-emerald-600', bg: 'bg-emerald-50', iconColor: 'text-emerald-500' };
  };

  return (
    <div className="fixed inset-0 z-[200] bg-secondary text-white flex flex-col font-sans overflow-hidden animate-in fade-in duration-300">
      {/* Header Terminal Responsivo */}
      <header className="h-auto md:h-24 bg-[#1d2733] border-b border-white/10 flex flex-col md:flex-row items-center justify-between px-6 md:px-10 py-4 md:py-0 shadow-2xl shrink-0 gap-4">
        <div className="flex items-center gap-4 md:gap-8 w-full md:w-auto">
           <div className="bg-primary p-3 md:p-4 rounded-xl md:rounded-2xl text-white shadow-lg shrink-0">
              <Tv size={24} className="md:w-8 md:h-8" />
           </div>
           <div className="min-w-0">
              <h1 className="text-xl md:text-3xl font-black uppercase tracking-tighter truncate">Terminal Alpha-01</h1>
              <div className="flex items-center gap-2">
                 <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                 <span className="text-[8px] md:text-[10px] font-black text-white/40 uppercase tracking-[0.2em] md:tracking-[0.4em]">Sincronização Live</span>
              </div>
           </div>
        </div>

        <div className="flex items-center justify-between md:justify-end w-full md:w-auto gap-4 md:gap-10 border-t md:border-t-0 border-white/5 pt-4 md:pt-0">
           {urgentCount > 0 && (
              <div className="flex bg-red-500/10 border border-red-500/20 px-3 md:px-6 py-2 rounded-xl items-center gap-2 md:gap-3 text-red-500 animate-pulse">
                 <ShieldAlert size={16} className="md:w-6 md:h-6" />
                 <span className="text-[9px] md:text-sm font-black uppercase">{urgentCount} Críticos</span>
              </div>
           )}
           <div className="text-right md:border-l md:border-white/10 md:pl-10 flex items-center gap-4 md:gap-8">
              <div className="hidden sm:block">
                <p className="text-2xl md:text-4xl font-black tracking-tighter leading-none">{time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
              <button onClick={onClose} className="p-2.5 md:p-4 bg-white/5 hover:bg-red-500/20 hover:text-red-500 rounded-xl md:rounded-2xl border border-white/10 transition-all">
                <X size={24} className="md:w-8 md:h-8" />
              </button>
           </div>
        </div>
      </header>

      {/* Grid de Cards Responsivo */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto scrollbar-hide grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-8 content-start">
        {terminalOrders.length > 0 ? terminalOrders.map((order) => {
          const client = clients.find(c => c.id === order.clientId);
          const isUrgent = order.prioridade === Priority.ALTA;
          const isLoading = order.status === OrderStatus.EM_CARREGAMENTO;
          const sla = getSLAData(order.horarioEntrega);
          const totalItems = order.materials.reduce((acc, m) => acc + m.qty, 0);

          return (
            <div 
              key={order.id} 
              className={`rounded-[32px] md:rounded-[50px] border-4 md:border-[6px] p-5 md:p-8 flex flex-col justify-between transition-all relative overflow-hidden bg-white shadow-2xl ${
                isUrgent ? 'border-red-500' : isLoading ? 'border-blue-500' : 'border-slate-100'
              }`}
            >
              {/* TOPO */}
              <div className="flex justify-between items-start mb-6 md:mb-8 gap-4">
                 <div className="flex-1 min-w-0">
                    <span className={`text-[8px] md:text-[11px] font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mb-1 md:mb-2 block ${isUrgent ? 'text-red-500' : 'text-primary'}`}>
                       {isUrgent ? 'EMBARQUE PRIORITÁRIO' : 'LOGÍSTICA AGENDADA'}
                    </span>
                    <h2 className="text-xl md:text-3xl font-black uppercase tracking-tight leading-[1.1] md:leading-[0.9] text-secondary italic break-words line-clamp-2">{order.nomeEvento}</h2>
                 </div>
                 <div className={`shrink-0 flex flex-col items-center justify-center w-16 h-16 md:w-28 md:h-28 rounded-2xl md:rounded-[36px] shadow-lg ${sla.bg} ${sla.color} border border-white animate-in zoom-in duration-500`}>
                    <Timer size={20} className={`mb-0.5 md:mb-1 ${sla.iconColor} md:w-8 md:h-8`} />
                    <span className="text-[7px] md:text-[10px] font-black uppercase">{sla.text}</span>
                    <span className="text-base md:text-xl font-black tracking-tighter leading-none">{sla.sub}</span>
                 </div>
              </div>

              {/* MEIO: PLACA E MOTORISTA */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
                 <div className="bg-slate-900 rounded-2xl md:rounded-[28px] p-3 md:p-5 shadow-lg border-b-2 md:border-b-4 border-slate-700 flex flex-col items-center justify-center">
                    <div className="flex items-center gap-1.5 mb-1">
                       <div className="w-3 h-1.5 md:w-4 md:h-2 bg-blue-600 rounded-sm"></div>
                       <span className="text-[7px] md:text-[8px] font-black text-white/40 uppercase tracking-widest">FROTA</span>
                    </div>
                    <span className="text-xl md:text-3xl font-black text-white tracking-[0.1em]">{order.placa || '---------'}</span>
                 </div>
                 
                 <div className="bg-slate-50 rounded-2xl md:rounded-[28px] p-3 md:p-5 shadow-inner border border-slate-200 flex flex-col justify-center px-4 md:px-6">
                    <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5 md:mb-1">Condutor</p>
                    <p className="text-base md:text-xl font-black text-secondary uppercase leading-none truncate">
                       {order.responsavelInterno.split(' ')[0] || '---'}
                    </p>
                 </div>
              </div>

              {/* MÉTRICAS */}
              <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-10">
                 <div className="bg-white border border-slate-100 p-3 md:p-5 rounded-2xl md:rounded-[32px] shadow-lg flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 text-primary rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                       <Box size={18} className="md:w-6 md:h-6" />
                    </div>
                    <div className="min-w-0">
                       <p className="text-[7px] md:text-[9px] font-black text-slate-400 uppercase">Volume</p>
                       <p className="text-lg md:text-2xl font-black text-secondary leading-none">{totalItems}</p>
                    </div>
                 </div>

                 <div className="bg-white border border-slate-100 p-3 md:p-5 rounded-2xl md:rounded-[32px] shadow-lg flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-50 text-emerald-500 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                       <Clock size={18} className="md:w-6 md:h-6" />
                    </div>
                    <div className="min-w-0">
                       <p className="text-[7px] md:text-[9px] font-black text-slate-400 uppercase">Saída</p>
                       <p className="text-lg md:text-2xl font-black text-secondary leading-none">{order.horarioEntrega}</p>
                    </div>
                 </div>
              </div>

              {/* RODAPÉ */}
              <div className="pt-4 md:pt-8 border-t border-slate-100 flex items-center justify-between gap-2">
                 <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className="p-2 md:p-3 bg-primary/5 text-primary rounded-lg md:rounded-xl shrink-0">
                       <Navigation size={14} className="md:w-[18px] md:h-[18px]" />
                    </div>
                    <div className="min-w-0">
                       <p className="text-[7px] md:text-[9px] font-black text-slate-400 uppercase leading-none mb-1">Localização</p>
                       <p className="text-xs md:text-base font-black text-secondary uppercase leading-none truncate">{client?.nomeLocal || '---'}</p>
                    </div>
                 </div>
                 <Badge className={`${isLoading ? 'bg-blue-600 animate-pulse' : 'bg-secondary'} text-white border-none px-2.5 md:px-4 py-1.5 md:py-2 font-black text-[7px] md:text-[9px] shrink-0`}>
                    {order.status.replace('_', ' ')}
                 </Badge>
              </div>
            </div>
          );
        }) : null}
      </main>

      {/* Footer com Legenda Compacta */}
      <footer className="h-auto md:h-20 bg-secondary border-t border-white/5 flex flex-col md:flex-row items-center justify-between px-6 md:px-10 py-4 md:py-0 shrink-0 gap-4">
         <div className="flex flex-wrap justify-center gap-4 md:gap-12">
            <Legend color="bg-red-500" label="SLA Crítico" />
            <Legend color="bg-emerald-500" label="No Prazo" />
         </div>
         <div className="flex items-center gap-2 md:gap-4 text-[7px] md:text-[9px] font-black text-white/20 uppercase tracking-[0.2em] md:tracking-[0.5em]">
            <span>EventLog Intelligence v4.5</span>
            <div className="w-1 h-1 bg-white/10 rounded-full"></div>
            <span>Sorocaba SP</span>
         </div>
      </footer>
    </div>
  );
};

const Legend = ({ color, label }: any) => (
  <div className="flex items-center gap-2 md:gap-3">
    <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${color} shadow-[0_0_10px_rgba(255,255,255,0.1)]`}></div>
    <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-white/50">{label}</span>
  </div>
);
